import React from "react";
import {useNavigate} from "react-router-dom";
import {Button, Typography} from "@mui/material";
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import LinearProgress, {linearProgressClasses} from '@mui/material/LinearProgress';
import {styled} from '@mui/material/styles';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import Divider from '@mui/material/Divider';
import GaugeChart from "./GaugeChart";
import RadialChart from "./RadialChart";
import * as util from "../utils/util";
import {
  BLOOD_TIME_SLOTS,
  getBloodCardStatus,
  getDeviceById,
  getGaugeValue,
  getMetricStatus,
  getMetricStatusText,
  getPrimaryMetric,
  getPressureStatus,
  getStatusMetric,
  getSummaryValue
} from "../utils/deviceInfo";
import { bloodRecentOf, recentOf, useHealthDevice } from "../utils/healthDeviceApi";

/** 'YYYY-MM-DD' → 'M.D' (오늘이면 빈 문자열) */
const measuredLabel = (date: string | null): string => {
  if (!date) return '';
  if (date === util.getDateByCustom('yyyy-MM-dd', 0)) return '';
  const [, m, d] = date.split('-');
  return `${Number(m)}.${Number(d)} 측정`;
};

// 선택한 기기 1대의 대표 측정값 카드
const HistoryDeviceValueCard = (props: any) => {
  const navigate = useNavigate();
  const device = getDeviceById(props.deviceId);
  // 훅은 조기 반환보다 먼저 — 카드는 '현재 상태' 요약이다
  const { data: deviceData } = useHealthDevice(0);

  if (device === undefined) {
    return null;
  }

  const isBlood = device.type === 'blood';
  const isPressure = device.type === 'pressure';
  const isWeight = device.type === 'weight';
  // 오늘 기록이 없으면 가장 최근 기록으로 물러난다 — 며칠 전에 측정했는데 0 이 뜨지 않게.
  // measuredDate 가 오늘이 아니면 아래에 측정일을 함께 표시한다.
  const { values: latest, date: measuredDate } = recentOf(deviceData, device, 0);
  const metric = getPrimaryMetric(device.id);

  // 혈당기는 가장 최근 시간대 측정값을 기준으로 노출 (상세 화면과 동일)
  const { values: bloodSlotValues, date: bloodDate } = bloodRecentOf(deviceData, 0);
  const bloodLatest = util.getLatestValue(bloodSlotValues);
  const bloodStatus = util.getPressureStatusAndColor(bloodLatest.type, bloodLatest.bloodValue);

  // 혈압계는 수축기 / 이완기를 함께 판정 (상세 화면과 동일)
  const targetPressure = { bloodPressureHigh: latest.systolic, bloodPressureLow: latest.diastolic };

  const summary = isBlood
    ? { text: String(bloodLatest.bloodValue), unit: metric.unit }
    : getSummaryValue(device.id, latest);

  // null = 미측정 (GaugeChart 가 '미측정'으로 표시)
  let gaugeValue: number | null = getGaugeValue(metric, latest[metric.key]);
  if (isBlood) {
    gaugeValue = bloodStatus.gaugeValue;
  } else if (isPressure) {
    // '|| 50' 을 붙이면 안 된다 — 미측정(null)이 정상 구간 한가운데로 바뀐다.
    // GaugeChart 가 null 을 받으면 '미측정'으로 표시한다.
    gaugeValue = util.getPercentValueForGauge(targetPressure);
  }

  // 상태 판정 항목 (체중계는 BMI 기준)
  const statusMetric = getStatusMetric(device.id);
  let status = getMetricStatus(statusMetric, latest[statusMetric.key]);
  if (isBlood) {
    status = getBloodCardStatus(bloodStatus);
  } else if (isPressure) {
    status = getPressureStatus(latest.systolic, latest.diastolic);
  }

  // 하단 진행바는 기존 카드들과 동일하게 '측정 완료도' 기준
  function getBorderProgressValue() {
    if (isBlood) {
      // 혈당기는 시간대 7개 중 측정한 횟수 비율 (기존 혈당 카드와 동일)
      const measuredCount = BLOOD_TIME_SLOTS.filter((slot) => bloodSlotValues[slot.key] > 0).length;
      return Math.round(measuredCount / BLOOD_TIME_SLOTS.length * 100);
    }
    if (isPressure) {
      // 혈압계는 수축기 / 이완기 둘 다 측정되어야 완료 (기존 혈압 카드와 동일)
      return (latest.systolic > 0 && latest.diastolic > 0) ? 100 : 0;
    }
    // 그 외는 대표 항목 측정 여부
    return latest[metric.key] > 0 ? 100 : 0;
  }

  function goDetail() {
    navigate('../history/HistoryDeviceDetail', { state: { deviceId: device.id } });
  }

  return (
    <Box alignItems="stretch" sx={Card_Style}>

      {/* 기기명은 칸보다 길면 말줄임(...) 한다 — 카드 폭은 기기 선택과 무관하게 고정이다 */}
      <Box sx={{ p: '8px 6px 8px 6px', minWidth: 0 }}>
        <Typography noWrap sx={{ textAlign: 'center', fontSize: 14, fontWeight: 700 }}>{device.typeName}</Typography>
        <Typography noWrap title={device.name} sx={{ textAlign: 'center', fontSize: 11, color: '#888' }}>{device.name}</Typography>
      </Box>
      <Divider />
      <Box sx={{ p: 0.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Stack justifyContent={'center'} alignItems={'center'} sx={{ flexGrow: 1 }}>
          <Box>
            {/* 체중계는 체중 기록 화면과 동일한 방사형 게이지 사용 */}
            {isWeight ? (
              <RadialChart value={latest.weight_kg} />
            ) : (
              <GaugeChart targetValue={gaugeValue} />
            )}

            {/* 방사형 게이지는 하단 여백이 있어 상태 텍스트를 끌어올림 */}
            <Typography component={'div'} sx={{ width: '100%', textAlign: 'center', color: status.color, fontSize: 16, fontWeight: 700, mt: isWeight ? '-16px' : 0 }}>
              {getMetricStatusText(statusMetric, status)}
            </Typography>
          </Box>

          <Button onClick={goDetail} sx={{ flexWrap: 'wrap', p: 0, pt: 1, pb: 0 }}>
            <Stack direction={'row'} alignItems={'baseline'}>
              <Typography sx={{ color: '#555', fontSize: 22, pr: '5px', fontWeight: 700 }}>{summary.text}</Typography>
              <Typography sx={{ color: '#888', fontSize: 14, textTransform: 'none' }}>{summary.unit}</Typography>
            </Stack>
          </Button>

          {/* 오늘 잰 값이 아니면 언제 잰 값인지 함께 보여준다 — 오늘 측정한 것으로 오해하지 않게 */}
          <Typography sx={{ height: 16, color: '#aaa', fontSize: 11, lineHeight: '16px' }}>
            {measuredLabel(isBlood ? bloodDate : measuredDate)}
          </Typography>

          <Box sx={{ pb: 2 }}>
            <Button onClick={goDetail} sx={{ p: 0, border: '1px solid #EF8322', borderRadius: 5 }}>
              <Typography sx={{ color: '#EF8322', fontSize: 14, fontWeight: 700, pl: 1.5 }}>상세보기</Typography>
              <ArrowForwardIosRoundedIcon sx={{ mr: 1, ml: .5, color: '#EF8322', fontSize: 14 }} />
            </Button>
          </Box>
        </Stack>
        <Divider />
        <Box sx={{ p: 0.5 }}>
          <Typography sx={{ fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', height: 32, color: '#555' }}> {status.message}</Typography>
        </Box>
      </Box>

      <BorderLinearProgress variant="determinate" value={getBorderProgressValue()}
        sx={{ borderRadius: '0 0 20px 20px', bgcolor: '#eaeaea' }}
      />
    </Box>
  );
}

const BorderLinearProgress = styled(LinearProgress)({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: '#eaeaea',
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: '#33cccc',
  },
});

const Card_Style = {
  borderRadius: '10px',
  background: '#fff',
  boxShadow: '0 0 6px  rgba(0,0,0,0.2)',

  flex: '0 50%',
  maxWidth: '100%',

  // 옆 카드와 높이를 맞추기 위해 세로 flex 로 구성 (하단 진행바는 항상 카드 맨 아래)
  height: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column'
};

export default HistoryDeviceValueCard;
